/**
 * Салбар — прототипийн `05 Branches.dc.html`-ээс хөрвүүлсэн.
 *
 * Бүтэц: салбарын карт (нэр + шошго + мөрүүд + прогресс) → шилжүүлэлт.
 *
 * ⚠️ Прототипийн картад «Энэ сард зарсан», «Орлого», «Сарын
 *    төлөвлөгөө %» гурав байсан:
 *      · эхний хоёр нь `orders` дээр тулгуурладаг — модуль хийгдээгүй
 *      · төлөвлөгөө нь схемд ОГТ БАЙХГҮЙ (target хүснэгт байхгүй)
 *    Хуурамч тоо гаргалгүй, салбарын бодит нөөц/ажилтан/өртгийг
 *    харуулав. Прогресс мөр нь «зарагдахад бэлэн» хувь.
 *
 * ⚠️ Шилжүүлэлт: `transfers` хүснэгт (ШЖ-код, төлөв, скан) нь тусдаа
 *    модуль. Одоо `item_movements`-ийн TRANSFER бичлэгийг өдөр,
 *    чиглэл, хүнээр бүлэглэж багц мэт харуулж байна.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatMnt, formatQty } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Branch, BranchReport, BranchTransferRow } from '../lib/types';
import { Empty, ErrorNote, Spinner } from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { Meter } from '../components/meter';

export function BranchesPage() {
  const { can, readOnly } = useAuth();
  // Салбар, байршил ҮҮСГЭХ нь «Тохиргоо → Салбар, агуулах» дээр.
  // Энэ хуудас нь зөвхөн тэдгээрийн ҮЙЛ АЖИЛЛАГААГ харуулна.
  const canSetup = can('ADMIN', 'WAREHOUSE') && !readOnly;

  const { data, isPending, error } = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Envelope<BranchReport>>('/reports/branches'),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const { branches, transfers } = data.data;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12.5px] text-ink-2">
          {formatQty(branches.length)} нэгж · нөөц, ажилтан, шилжилт
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {canSetup ? (
            <Link
              to="/setup/stores"
              className="rounded-full border border-hair px-4 py-2 text-[12.5px]
                whitespace-nowrap transition hover:bg-well"
            >
              Салбар, байршил удирдах →
            </Link>
          ) : null}
          <Link
            to="/transfers"
            className="rounded-full bg-accent px-4 py-2 text-[12.5px] text-white transition
              hover:bg-accent-fill-hover active:translate-y-[0.5px]"
          >
            Бараа шилжүүлэх
          </Link>
        </div>
      </div>

      {/* ─────────── Салбарын карт ─────────── */}
      <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
        {branches.map((b) => <BranchCard key={b.id} b={b} />)}
      </div>

      {/* ─────────── Шилжүүлэлт ─────────── */}
      <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-base font-semibold">Шилжүүлэлт</h2>
          <span className="text-[12.5px] text-ink-2">барааны хөдөлгөөнөөр</span>
          <span className="text-[11.5px] text-ink-3">
            шилжүүлэлтийн дугаар, төлөв удахгүй нэмэгдэнэ
          </span>
        </div>

        <DataTable
          rows={transfers}
          columns={branchTransferColumns()}
          rowKey={(t) => `${t.day}|${t.from}|${t.to}|${t.by}`}
          empty={<Empty>Шилжүүлэлт бүртгэгдээгүй</Empty>}
        />
      </section>

    </div>
  );
}

function BranchCard({ b }: { b: Branch }) {
  const pct = b.items ? Math.round((b.sellable / b.items) * 100) : 0;

  // Зарагдах бараагүй жижиглэнгийн салбар л анхаарал татна.
  // Агуулахад зарагдахгүй нөөц (хүлээн авалт, тусгаарлалт) байх нь ХЭВИЙН.
  const noStock = b.type === 'RETAIL' && b.sellable === 0;

  const tag = b.unlabeled > 0
    ? { text: `Шошгогүй ${formatQty(b.unlabeled)}`, cls: 'bg-alert-bg text-alert-ink' }
    : noStock
      ? { text: 'Нөөц алга', cls: 'bg-alert-bg text-alert-ink' }
      : { text: 'Хэвийн', cls: 'bg-accent-100 text-accent-700' };

  return (
    <div className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex items-baseline gap-2.5">
        <span className="text-base font-semibold">{b.name}</span>
        <span className={`ml-auto rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${tag.cls}`}>
          {tag.text}
        </span>
      </div>

      <span className="-mt-2 text-[11.5px] text-ink-3">
        {b.type === 'WAREHOUSE' ? 'Агуулах' : 'Жижиглэн'} · {b.code}
        {b.address ? ` · ${b.address}` : ''}
      </span>

      <dl className="flex flex-col gap-[7px] text-[13px]">
        <Row label="Байршил" value={formatQty(b.locations)} />
        <Row label="Нөөц" value={`${formatQty(b.items)} ш`} />
        <Row label="Зарагдахад бэлэн" value={`${formatQty(b.sellable)} ш`} />
        <Row label="Ажилтан" value={formatQty(b.staff)} />
        <Row
          label="Нөөцийн өртөг"
          value={b.landedCostMnt === null ? '•••' : formatMnt(b.landedCostMnt)}
        />
      </dl>

      <div className="flex flex-col gap-[5px] border-t border-hair pt-2.5">
        <div className="flex gap-2.5 text-xs text-ink-2">
          <span>Зарагдахад бэлэн</span>
          <span className="num ml-auto">{pct}%</span>
        </div>
        <Meter value={pct} tone={noStock ? 'alert' : 'accent'} label={`Зарагдахад бэлэн ${pct}%`} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="text-ink-2">{label}</dt>
      <dd className="num ml-auto whitespace-nowrap">{value}</dd>
    </div>
  );
}

/** Салбар хоорондын хөдөлгөөн — 640px-ээс доош өдөр тус бүрийн карт. */
function branchTransferColumns(): Column<BranchTransferRow>[] {
  return [
    {
      key: 'day',
      header: 'Огноо',
      cell: (t) => <span className="num text-ink-2">{formatDate(t.day)}</span>,
    },
    // Байршлын кирилл нэр урт — `nowrap` тавихгүй, боогдоно
    { key: 'from', header: 'Хаанаас', cell: (t) => <span className="text-ink-2 text-pretty">{t.from}</span> },
    { key: 'to', header: 'Хаана', mobile: 'title', cell: (t) => <span className="text-pretty">{t.to}</span> },
    {
      key: 'count',
      header: 'Тоо',
      align: 'right',
      mobile: 'value',
      cell: (t) => `${formatQty(t.count)} ш`,
    },
    { key: 'by', header: 'Хэн', cell: (t) => <span className="text-ink-2 text-pretty">{t.by}</span> },
  ];
}
